import type {
  Message,
  MessageSendParams,
  Part,
  Task,
  TaskArtifactUpdateEvent,
  TaskStatusUpdateEvent,
} from "@a2a-js/sdk";
import {
  A2AClient,
  type HttpHeaders,
  createAuthenticatingFetchWithRetry,
} from "@a2a-js/sdk/client";
import { v4 as uuidv4 } from "uuid";

import { authService } from "services/auth";

import {
  logCancelTaskCall,
  logCancelTaskError,
  logCancelTaskSuccess,
} from "./copilotLogs";

const containsThrottlingHint = (value: unknown): boolean => {
  try {
    const text =
      typeof value === "string"
        ? value.toLowerCase()
        : JSON.stringify(value).toLowerCase();
    return (
      text.includes("too many tokens") ||
      text.includes("throttling") ||
      text.includes(" 429")
    );
  } catch {
    return false;
  }
};

const FRIENDLY_THROTTLED_MESSAGE =
  "Too many requests. Please wait a few seconds and try again.";

const toFriendlyIfInternal = (
  message: unknown,
  data: unknown
): string | null => {
  if (message === "Internal error" && containsThrottlingHint(data)) {
    return FRIENDLY_THROTTLED_MESSAGE;
  }
  return null;
};

const a2aFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const response = await fetch(input, init);

  // For OK responses, just return as-is
  if (response.ok) {
    return response;
  }

  // For error responses, rewrite the body to add 'error' field from 'title'
  try {
    const clonedResponse = response.clone();
    const errorBody = await clonedResponse.text();
    const errorData = JSON.parse(errorBody);

    // If the response has a 'title' but no 'error' field, add it
    if (errorData?.title && !errorData?.error) {
      const rewrittenData = {
        ...errorData,
        error: {
          message: errorData.title,
          code: errorData.status || response.status,
          data: errorData,
        },
      };

      // Create a new Response with the rewritten body
      return new Response(JSON.stringify(rewrittenData), {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    }
  } catch {
    // If we can't parse or rewrite, return the original response
  }

  return response;
};

const authFetch = createAuthenticatingFetchWithRetry(a2aFetch, {
  headers: async (): Promise<HttpHeaders> => {
    const token = await authService.getToken();
    const headers: HttpHeaders = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    return headers;
  },
  shouldRetryWithHeaders: async (
    _req: RequestInit,
    res: Response
  ): Promise<HttpHeaders | undefined> => {
    if (res.status === 401 || res.status === 403) {
      const token = await authService.getToken();
      return token ? { Authorization: `Bearer ${token}` } : undefined;
    }
    return undefined;
  },
});

const baseApiUrl = `${process.env.REACT_APP_API_ENDPOINT_URL}/a2a/v1`;
const agentCardUrl = `${baseApiUrl}/.well-known/agent.json`;

let a2aClient: A2AClient | null = null;

const getA2AClient = async (): Promise<A2AClient> => {
  if (a2aClient) {
    return Promise.resolve(a2aClient);
  }
  a2aClient = await A2AClient.fromCardUrl(agentCardUrl, {
    fetchImpl: authFetch,
  });
  return a2aClient;
};

export const sendMessageStream = async function* sendMessageStream({
  contextId,
  message,
  metadata,
}: {
  contextId?: string;
  message: string;
  metadata?: Record<string, unknown>;
}): AsyncGenerator<
  Message | Task | TaskStatusUpdateEvent | TaskArtifactUpdateEvent,
  void,
  undefined
> {
  const parts: Part[] = [
    {
      kind: "text",
      text: message,
    },
  ];

  const params: MessageSendParams = {
    message: {
      kind: "message",
      role: "user",
      messageId: uuidv4(),
      ...(contextId ? { contextId } : {}),
      ...(metadata ? { metadata } : {}),
      parts,
    },
  };

  const a2aClient = await getA2AClient();
  const stream = a2aClient.sendMessageStream(params);

  try {
    for await (const event of stream) {
      yield event;
    }
  } catch (err: any) {
    const friendly = toFriendlyIfInternal(err?.message, err?.data);
    if (friendly) {
      throw new Error(friendly);
    }
    throw err;
  }
};

export const cancelTask = async (taskId: string) => {
  try {
    try {
      logCancelTaskCall({ taskId });
    } catch (_err) {
      // ignore logging errors
    }
    const a2aClient = await getA2AClient();
    await a2aClient.cancelTask({ id: taskId });
    try {
      logCancelTaskSuccess({ taskId });
    } catch (_err) {
      // ignore logging errors
    }
  } catch (err) {
    try {
      const message = (err as any)?.message || "Unknown error";
      logCancelTaskError({ taskId, errorMessage: message });
    } catch (_err) {
      // ignore logging errors
    }
    // eslint-disable-next-line no-console
    console.warn("cancelTask failed", err);
  }
};
